import FillInContent from "components/Dashboard/DoctorDashboard/notes-and-forms/components/fill-in-content/FillInContent";
import { BLOCK_TYPE_MODES } from "components/Dashboard/DoctorDashboard/notes-and-forms/constants/blockTypeModes";
import withAddendums from "components/Dashboard/DoctorDashboard/notes-and-forms/hocs/withAddendums";
import withBlockAttachments from "components/Dashboard/DoctorDashboard/notes-and-forms/hocs/withBlockAttachments";
import ClientServiceAndDiagnosis from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/note-fill-in/components/client-service-and-diagnosis";
// import { useNoteAttachmentsFolder } from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/note-fill-in/hooks/useNoteAttachmentsFolder";
import getFullNoteDataByNoteId from "components/Dashboard/DoctorDashboard/notes-and-forms/utils/getFullNoteDataByNoteId";
import {
  DISABLE_OLD_TEMPLATE_CONTENT,
  ENABLE_NEW_TEMPLATE_CONTENT,
} from "constants/featureFlags";
import TemplateContent from "notes-and-forms-v2/components/template-content/TemplateContent";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./NoteView.module.scss";

interface NoteViewProps {
  note: NoteDataInfo;
}

// TODO: Refactor, improve styling
// The whole content of this file is copied from NotePrintOrDownloadTrigger (the logic part) and PrintLayout (the UI part)
// Memoized values for metaDataForDisplay, addendums, noteCodesForDisplay are used to do the conversions that PrintLayout applies
// to it's props before passing them to FillInContent, ClientServiceAndDiagnosis, and withBlockAttachments/withAddendums HOCs.

// Layout issues:
// Some elements will expand the note content horizontally so that it doesn't fit inside it's container,
// for instance when a treatment plan has reminder set to "on date" (Example: session with test patient Ashley Fennimore on January 25, 11:21 EST)
// or treatment plan frequency is a single, long word (Example: session with test patient Ashley Fennimore on January 26, 8:25 EST)
// Others, like options in "Short Term Goals for Individuals with Suicidal Problems" section of Suicide Risk Assessment note
// will be truncated instead. Example: session with test patient Ashley Fennimore on January 12

// Other styling issues:
// Notes don't work well when "night mode" is enabled in the video call's bottom bar
// Also, obviously, the styling doesn't match the new designs at this stage

const NoteView = ({ note }: NoteViewProps) => {
  const [metaData, setMetaData] = useState<Partial<NoteMetaData>>();
  const [secureData, setSecureData] = useState<Partial<NoteData>>();
  // const { attachments } = useNoteAttachmentsFolder(metaData, secureData);

  const [data, setData] = useState<Record<string, any>>();

  const [signedAddendums, setSignedAddendums] = useState<ParsedNoteAddendum[]>(
    [],
  );
  const [unsignedAddendum, setUnsignedAddendum] =
    useState<Partial<ParsedNoteAddendum>>();

  const [noteCodes, setNoteCodes] = useState<NoteCodes>({});
  const [signedAddendumNoteCodes, setSignedAddendumNoteCodes] = useState<
    NoteCodes | undefined | null
  >();

  const handlePrintOrDownload = useCallback(async () => {
    const {
      metaData,
      secureData,
      data,
      noteCodes,
      signedAddendums,
      unsignedAddendum,
      signedAddendumNoteCodes,
    } = await getFullNoteDataByNoteId(note.note_data_id);

    setMetaData(metaData);
    setSecureData(secureData);
    setData(data);
    setNoteCodes(noteCodes);
    setSignedAddendums(signedAddendums ?? []);
    setUnsignedAddendum(unsignedAddendum);
    setSignedAddendumNoteCodes(signedAddendumNoteCodes);
  }, [note]);

  useEffect(() => {
    handlePrintOrDownload();
  }, [handlePrintOrDownload]);

  const metaDataForDisplay = useMemo(() => {
    if (!metaData || !secureData) {
      return null;
    }
    return { ...metaData, ...secureData };
  }, [metaData, secureData]);

  const addendums = useMemo(
    () => [
      ...signedAddendums,
      {
        ...unsignedAddendum?.addendum,
        addendum: unsignedAddendum?.addendum,
      },
    ],
    [signedAddendums, unsignedAddendum],
  );

  const noteCodesForDisplay = useMemo(
    () => ({
      ...noteCodes,
      ...signedAddendumNoteCodes,
      ...unsignedAddendum?.client_service_and_diagnosis,
    }),
    [noteCodes, signedAddendumNoteCodes, unsignedAddendum],
  );

  const wrapped = useCallback(
    (children: JSX.Element) =>
      withBlockAttachments(
        "note",
        withAddendums("note", children, {
          canAdd: false,
          addendums: (addendums as any) || [],
        }),
        {
          canChange: false,
          attachments: /* attachments || */ {},
        },
      ),
    [
      addendums,
      /* attachments, */
    ],
  );

  return (
    <div className={styles.container}>
      <h4>{note.note_schema_title}</h4>

      {ENABLE_NEW_TEMPLATE_CONTENT &&
        wrapped(
          <TemplateContent
            templateElements={metaDataForDisplay?.schema_v2?.elements ?? []}
            templateData={data ?? {}}
            isLocked={true}
            showCopyToClipboard
          />,
        )}

      {metaDataForDisplay?.[`note_json_schema`] &&
        metaDataForDisplay?.[`note_ui_schema`] &&
        !DISABLE_OLD_TEMPLATE_CONTENT &&
        wrapped(
          <FillInContent
            disabled={true}
            jsonSchema={metaDataForDisplay[`note_json_schema`]}
            uiSchema={metaDataForDisplay[`note_ui_schema`]}
            formData={data}
            mode={BLOCK_TYPE_MODES.print}
          />,
        )}
      {noteCodesForDisplay && (
        <>
          <hr style={{ borderTop: "1px solid #adccff" }} />
          <ClientServiceAndDiagnosis
            value={noteCodesForDisplay}
            readOnly={true}
          />
        </>
      )}
    </div>
  );
};

export default NoteView;
